import { saveUploadBatch, getUploadRecord, getUploadRecords, claimUpload, finishUpload, renewUpload, bindUploadTarget } from './offlineStorage';
import { deliverIncident } from './submissionQueue';
import { requireSession, readSession, sessionIsCurrent, operationId, SESSION_CHANGED } from './submissionSession';

const files = new Map(), flights = new Map();
let rejectedToken = null;
const fileKey = (owner, id) => `${owner}:${id}`;
const clearFiles = () => files.clear();
if (typeof window !== 'undefined') {
  window.addEventListener(SESSION_CHANGED, clearFiles);
  window.addEventListener('storage', event => { if (!event.key || ['rv_token', 'rv_user'].includes(event.key)) clearFiles(); });
}
const message = (body, fallback) => typeof body?.message === 'string' ? body.message : fallback;
const receiptValid = (video, row) => Number.isSafeInteger(video?.id) && video.id > 0 &&
  (row.payload.unassigned === true ? video.incident == null : Number(video?.incident?.id) === row.payload.incidentId) && video.recordedFile === row.file.name &&
  video.fileSizeBytes === row.file.size && !video.queued && !video.offline;

export async function prepareUploads(inputs, token) {
  const session = requireSession(token);
  const existing = await getUploadRecords(session.ownerUserId);
  for (const input of inputs) {
    const row = existing.find(row => row.operationId === input.operationId);
    if (row?.incidentLocalId && !row.payload.incidentId) await bindUploadTarget(row.localId, session.ownerUserId);
  }
  const rows = await saveUploadBatch(inputs, token);
  if (sessionIsCurrent(session)) rows.forEach((row, index) => { if (row.status !== 'accepted') files.set(fileKey(session.ownerUserId, row.localId), inputs[index].file); });
  return rows;
}
export async function attachOriginalFile(localId, file, token) {
  const session = requireSession(token), row = await getUploadRecord(localId, session.ownerUserId);
  if (!row) throw new Error('Sign in as this recording’s owner to recover it.');
  // Mobile/cloud pickers can change lastModified for identical bytes. The server
  // verifies the retained content hash; file attributes alone never prove identity.
  if (row.file.name !== file?.name || row.file.size !== file?.size)
    throw new Error('Choose the original recording with the same filename and size. Saved details have not changed; the server checks received bytes.');
  if (!sessionIsCurrent(session)) throw new Error('Account changed. The recording remains with its original owner.');
  files.set(fileKey(session.ownerUserId, row.localId), file); return row;
}

export async function deliverUpload(localId, token, { sendFile = false, onProgress } = {}) {
  const session = requireSession(token), key = fileKey(session.ownerUserId, localId);
  if (flights.has(key)) return flights.get(key);
  const task = deliver(localId, session, sendFile, onProgress).finally(() => flights.delete(key));
  flights.set(key, task); return task;
}
async function deliver(localId, session, sendFile, onProgress) {
  const existing = await getUploadRecord(localId, session.ownerUserId);
  if (!existing) throw new Error('This upload is unavailable for the current account.');
  if (existing.status === 'accepted') {
    files.delete(fileKey(session.ownerUserId, localId));
    return { accepted: true, record: existing, video: existing.receipt, currentSession: sessionIsCurrent(session) };
  }
  if (!navigator.onLine || session.expired || rejectedToken === session.token || !sessionIsCurrent(session))
    return { accepted: false, record: existing, message: 'Saved on this device. Reconnect and sign in as its owner to continue.' };
  const lease = operationId(); let row = await claimUpload(localId, session.ownerUserId, lease);
  if (!row) return { accepted: false, record: existing, message: 'Upload confirmation is already in progress. Saved work is retained.' };
  let changes;
  const identity = { deviceId: row.deviceId, operationId: row.operationId };
  const renewal = setInterval(() => { renewUpload(localId, session.ownerUserId, lease).catch(() => {}); }, 30000);
  async function call(route, body) {
    if (!sessionIsCurrent(session)) throw new Error('Account changed. Saved work remains with its original owner.');
    const response = await fetch('/api/videos/' + route, { method: body ? 'POST' : 'GET', headers: {
      Authorization: `Bearer ${session.token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined, redirect: 'error', cache: 'no-store', signal: AbortSignal.timeout(20000) });
    let data; try { data = await response.json(); } catch { data = null; }
    if (response.status === 401) rejectedToken = session.token;
    return { status: response.status, body: data };
  }
  function checkError(result) {
    if ([400, 401, 403, 409, 422].includes(result.status)) {
      const error = new Error(message(result.body, result.status === 401 ? 'Sign in again as the recording’s owner.' : 'The saved upload requires review. Keep the original recording.'));
      error.status = result.status; throw error;
    }
  }
  async function accept(result) {
    if (result.status !== 200 || !receiptValid(result.body, row)) return null;
    const receipt = { id: result.body.id, recordedFile: result.body.recordedFile, fileSizeBytes: result.body.fileSizeBytes,
      timestamp: result.body.timestamp, cameraType: result.body.cameraType,
      incident: result.body.incident ? { id: result.body.incident.id, cadId: result.body.incident.cadId, isArchived: result.body.incident.isArchived === true } : null };
    const saved = await finishUpload(localId, session.ownerUserId, lease, { status: 'accepted', receipt,
      acceptedAt: new Date().toISOString(), lastError: null, lastHttpStatus: 200 });
    if (!saved) throw new Error('Server accepted the recording; local confirmation is unconfirmed. Check its saved receipt again.');
    files.delete(fileKey(session.ownerUserId, localId));
    return { accepted: true, record: saved, video: result.body, currentSession: sessionIsCurrent(session) };
  }
  let transferred = false, reconciliationAttempted = false;
  async function reconcileReceipt() {
    reconciliationAttempted = true;
    for (const delay of [0, 1000, 3000]) {
      if (!sessionIsCurrent(session) || readSession()?.expired) return null;
      if (delay) await new Promise(resolve => setTimeout(resolve, delay));
      try {
        const result = await call('upload-receipt?' + new URLSearchParams({ ...identity, __cad_read: operationId() }));
        const accepted = await accept(result);
        if (accepted) return accepted;
        if ([401, 403, 409, 422].includes(result.status)) return null;
      } catch { /* retain the saved identity and original file */ }
    }
    return null;
  }
  try {
    if (!row.payload.incidentId && row.incidentLocalId) {
      const call = await deliverIncident(row.incidentLocalId, session.token);
      if (!call.accepted) { const error = new Error('The call is awaiting confirmation. Its recording details are saved with it; confirm the original call first.'); error.awaitingCall = true; throw error; }
      const bound = await bindUploadTarget(localId, session.ownerUserId);
      if (!bound) throw new Error('The confirmed call association could not be saved. Keep the original recording.');
      row = bound;
    }
    const capability = await call('storage-status?__cad_read=' + operationId()); checkError(capability);
    if (capability.status !== 200 || capability.body?.multipartUploadProtocol !== 1)
      throw new Error('Saved locally. The server does not yet support safe upload recovery. No recording bytes were sent.');
    if (row.payload.unassigned === true && capability.body?.batchUploadProtocol !== 1)
      throw new Error('The server does not support unassigned video intake yet. No recording bytes were sent.');
    let result = await call('upload-receipt?' + new URLSearchParams({ ...identity, __cad_read: operationId() }));
    let accepted = await accept(result); if (accepted) return accepted; checkError(result);
    if (result.status === 200) throw new Error('Upload receipt was incomplete. Keep the original recording and check confirmation again.');
    let needsFile = result.status === 404;
    if (result.status === 202) {
      result = await call('upload-recover', identity); accepted = await accept(result); if (accepted) return accepted; checkError(result);
      if (result.status === 200) throw new Error('Recovery receipt was incomplete. Recording remains pending.');
      needsFile = result.status === 202 && result.body?.needsFile === true && result.body?.processing === false;
    } else if (result.status !== 404) throw new Error(message(result.body, 'Upload confirmation is unavailable. No recording bytes were resent.'));
    const file = files.get(fileKey(session.ownerUserId, localId));
    if (needsFile && sendFile && file) {
      if (!sessionIsCurrent(session) || readSession()?.expired) throw new Error('Sign in again as the owner before sending this recording.');
      const form = new FormData(); form.append('videoFile', file, row.file.name);
      for (const [key, value] of Object.entries({ ...row.payload, ...identity })) if (value !== undefined && value !== null) form.append(key, String(value));
      transferred = true;
      result = await transmit(form, session, onProgress);
      if (result.status === 401) rejectedToken = session.token;
      accepted = await accept(result); if (accepted) return accepted;
      if (![400, 401, 403, 409, 422].includes(result.status)) { accepted = await reconcileReceipt(); if (accepted) return accepted; }
      checkError(result);
      if (result.status === 200) throw new Error('Recording transfer finished but its receipt was incomplete. Check confirmation before retrying.');
      changes = { status: 'awaiting-confirmation', lastHttpStatus: result.status,
        lastError: message(result.body, 'Recording is not yet confirmed. Check its receipt; the same saved identifier will be retained.') };
    } else changes = { status: needsFile ? 'needs-file' : 'awaiting-confirmation', lastHttpStatus: result.status,
      lastError: needsFile ? 'Select the original recording, then choose Continue upload. Its saved call and details will be used.' :
        message(result.body, 'Awaiting server confirmation. Checking does not resend recording bytes.') };
  } catch (error) {
    if (transferred && !reconciliationAttempted && ![400, 401, 403, 409, 422].includes(error.status)) { const accepted = await reconcileReceipt(); if (accepted) return accepted; }
    changes = { status: error.awaitingCall ? 'awaiting-call' : [400, 403, 409, 422].includes(error.status) ? 'needs-review' : 'awaiting-confirmation',
      lastHttpStatus: error.status || null, lastError: error.message || 'Upload was not confirmed. Keep the original recording.' };
  } finally { clearInterval(renewal); }
  const saved = await finishUpload(localId, session.ownerUserId, lease, changes);
  return { accepted: false, record: saved || row, message: changes.lastError, currentSession: sessionIsCurrent(session) };
}

// XHR provides real transfer progress without persisting or replacing credentials.
// A session change aborts only this transfer; its identifier remains recoverable.
function transmit(form, session, onProgress) {
  return new Promise((resolve, reject) => {
    if (!sessionIsCurrent(session)) { reject(new Error('Account changed before recording transmission.')); return; }
    const request = new XMLHttpRequest();
    const changed = () => { if (!sessionIsCurrent(session)) request.abort(); };
    const cleanup = () => { window.removeEventListener(SESSION_CHANGED, changed); window.removeEventListener('storage', changed); };
    const failed = () => { cleanup(); reject(new Error('Recording transfer was interrupted. Check its saved receipt before sending bytes again.')); };
    request.open('POST', '/api/videos/upload'); request.setRequestHeader('Authorization', `Bearer ${session.token}`);
    request.timeout = 2 * 60 * 60 * 1000;
    request.upload.onprogress = event => { if (sessionIsCurrent(session)) onProgress?.({ loaded: event.loaded, total: event.lengthComputable ? event.total : 0 }); };
    request.onload = () => { cleanup(); let body; try { body = JSON.parse(request.responseText); } catch { body = null; } resolve({ status: request.status, body }); };
    request.onerror = request.onabort = request.ontimeout = failed;
    window.addEventListener(SESSION_CHANGED, changed); window.addEventListener('storage', changed);
    if (!sessionIsCurrent(session)) { cleanup(); reject(new Error('Account changed before recording transmission.')); return; }
    request.send(form);
  });
}
export async function recoverOwnedUploads(token) {
  const session = requireSession(token), rows = await getUploadRecords(session.ownerUserId), results = [];
  for (const row of rows.filter(r => !['accepted', 'needs-review', 'needs-file', 'pending'].includes(r.status)).slice(0, 10)) {
    if (!sessionIsCurrent(session)) break;
    results.push(await deliverUpload(row.localId, token));
  }
  return results;
}
